/**
 * i18n (Internationalization) State Management
 *
 * We use atomWithStorage with `getOnInit: true` to ensure we read the stored
 * locale before initializing. This is critical for the Google Maps integration
 * since:
 *
 * 1. Maps API only allows loading once per page with a single language setting
 * 2. Without `getOnInit`, we would always get the default 'en' locale first
 * 3. When the stored locale is later read, Maps would try to reload with new
 *    language
 * 4. This causes the "Loader must not be called again with different options"
 *    error
 *
 * By using `getOnInit`, we wait for the stored locale to be read before any
 * initialization, preventing the double-load scenario.
 */
import { MessageFormatElement } from "react-intl";
import { atomWithStorage } from "jotai/utils";

import { SupportedLocale } from "../../scripts/i18n/types";

// Get initial locale from browser or fallback to English
const getBrowserLocale = (): SupportedLocale => {
  // Get base language code, ignoring region (en-US -> en)
  const browserLang = navigator.language.split("-")[0];
  return Object.values(SupportedLocale).includes(browserLang as SupportedLocale)
    ? (browserLang as SupportedLocale)
    : SupportedLocale.English;
};

export const localeAtom = atomWithStorage<SupportedLocale>(
  "app-locale",
  getBrowserLocale(),
  undefined,
  { getOnInit: true },
);

export const translationsAtom = atomWithStorage<
  Record<string, MessageFormatElement[]>
>("app-translations", {}, undefined, { getOnInit: true });
